import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Lock, Calendar, Sparkles } from 'lucide-react';
import { AchievementDefinition } from './achievementDefinitions';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface AchievementDetailModalProps {
  achievement: AchievementDefinition | null;
  isUnlocked: boolean;
  unlockedAt?: string;
  progress?: { current: number; target: number };
  onClose: () => void;
}

export const AchievementDetailModal: React.FC<AchievementDetailModalProps> = ({
  achievement,
  isUnlocked,
  unlockedAt,
  progress,
  onClose
}) => {
  if (!achievement) return null;

  const progressPercent = progress ? Math.min((progress.current / progress.target) * 100, 100) : 0;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-zinc-900 border border-white/10 rounded-3xl p-6 max-w-sm w-full relative overflow-hidden"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/60 hover:bg-white/20 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Background glow */}
          {isUnlocked && (
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-amber-500/20 rounded-full blur-3xl" />
          )}

          <div className="flex flex-col items-center text-center relative">
            {/* Badge */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', delay: 0.1 }}
              className={`w-24 h-24 rounded-2xl flex items-center justify-center mb-4 ${
                isUnlocked
                  ? 'bg-gradient-to-br from-amber-500/30 to-orange-600/30 border-2 border-amber-400'
                  : 'bg-white/5 border border-white/10'
              }`}
            >
              <span className={`text-5xl ${!isUnlocked && 'grayscale opacity-50'}`}>
                {achievement.badge}
              </span>
            </motion.div>

            {/* Status badge */}
            <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium mb-3 ${
              isUnlocked
                ? 'bg-green-500/20 text-green-400'
                : 'bg-white/10 text-white/60'
            }`}>
              {isUnlocked ? (
                <>
                  <Check className="w-3 h-3" />
                  Desbloqueado
                </>
              ) : (
                <>
                  <Lock className="w-3 h-3" />
                  Bloqueado
                </>
              )}
            </div>

            {/* Name and description */}
            <h3 className="text-xl font-bold text-white mb-2">{achievement.name}</h3>
            <p className="text-white/60 text-sm mb-4">{achievement.description}</p>

            {/* Requirement */}
            <div className="bg-white/5 rounded-xl p-3 w-full mb-4">
              <p className="text-xs text-white/40 mb-1">Requisito</p>
              <p className="text-sm text-white">{achievement.requirement}</p>
            </div>

            {/* Progress or date */}
            {isUnlocked && unlockedAt ? (
              <div className="flex items-center gap-2 text-white/60 text-sm">
                <Calendar className="w-4 h-4" />
                <span>
                  Desbloqueado el {format(new Date(unlockedAt), "d 'de' MMMM, yyyy", { locale: es })}
                </span>
              </div>
            ) : progress && progress.target > 0 ? (
              <div className="w-full">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-white/60">Progreso</span>
                  <span className="text-sm text-white">
                    {progress.current} / {progress.target}
                  </span>
                </div>
                <div className="w-full h-3 bg-white/10 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progressPercent}%` }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                    className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full"
                  />
                </div>
                <p className="text-xs text-white/40 mt-2">
                  {progressPercent >= 100 
                    ? '¡Ya cumples el requisito! El logro se desbloqueará pronto.'
                    : `Te falta ${Math.round(100 - progressPercent)}% para desbloquear`
                  }
                </p>
              </div>
            ) : null}

            {/* Points */}
            <div className="flex items-center gap-2 mt-4 px-4 py-2 bg-gradient-to-r from-amber-500/20 to-orange-600/20 rounded-xl border border-amber-500/30">
              <Sparkles className="w-4 h-4 text-amber-400" />
              <span className="text-amber-400 font-semibold">+{achievement.points} puntos</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AchievementDetailModal;
